import { t } from "i18next";
import * as z from "zod";

import { PREFERRED_LANGUAGES } from "@/features/shared/api/enums";

/**
 * Schémas des dialogues « simulateurs » (`POST /NotificationEvents/simulate-*`).
 * Un schéma par événement — factories mémoïsées sur `i18n.language` au
 * niveau du composant (règle projet). Les corps de requête suivent la
 * collection Postman (guide §6.7). Les identifiants d’entité (`partyId`,
 * `keycloakUserId`, `agentPartyId`) sont des UUID en sortie (selects
 * searchables). `eventId` est un UUID client de déduplication, pas une
 * entité listable.
 */

const uuid = () =>
  z
    .string()
    .trim()
    .min(1, { message: t("requiredFieldError") })
    .uuid({ message: t("invalidFieldError") });

const preferredLanguage = () =>
  z.enum(PREFERRED_LANGUAGES, {
    errorMap: () => ({ message: t("requiredFieldError") }),
  });

const recipientAddress = () =>
  z
    .string()
    .trim()
    .min(1, { message: t("requiredFieldError") });

const isoDateTime = () =>
  z
    .string()
    .trim()
    .min(1, { message: t("requiredFieldError") });

const isoDate = () =>
  z
    .string()
    .trim()
    .min(1, { message: t("requiredFieldError") })
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: t("invalidFieldError") });

/** Simulate KYC approuvé — palier obtenu + horodatage. */
export const createSimulateKycApprovedSchema = () =>
  z.object({
    eventId: uuid(),
    partyId: uuid(),
    keycloakUserId: uuid(),
    recipientAddress: recipientAddress(),
    preferredLanguage: preferredLanguage(),
    level: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    approvedAt: isoDateTime(),
  });

export type SimulateKycApprovedFormValues = z.infer<
  ReturnType<typeof createSimulateKycApprovedSchema>
>;

/** Simulate KYC rejeté — motif inclus. */
export const createSimulateKycRejectedSchema = () =>
  z.object({
    eventId: uuid(),
    partyId: uuid(),
    keycloakUserId: uuid(),
    recipientAddress: recipientAddress(),
    preferredLanguage: preferredLanguage(),
    reason: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
  });

export type SimulateKycRejectedFormValues = z.infer<
  ReturnType<typeof createSimulateKycRejectedSchema>
>;

/** Simulate KYC expiré — palier + date d'expiration. */
export const createSimulateKycExpiredSchema = () =>
  z.object({
    eventId: uuid(),
    partyId: uuid(),
    recipientAddress: recipientAddress(),
    preferredLanguage: preferredLanguage(),
    level: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    expiryDate: isoDate(),
  });

export type SimulateKycExpiredFormValues = z.infer<
  ReturnType<typeof createSimulateKycExpiredSchema>
>;

/** Simulate transaction aboutie — client + éventuellement agent (commission). */
export const createSimulateTransactionCompletedSchema = () =>
  z.object({
    eventId: uuid(),
    transactionReference: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    productCode: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    partyId: uuid(),
    recipientAddress: recipientAddress(),
    preferredLanguage: preferredLanguage(),
    agentPartyId: uuid().or(z.literal("")),
    agentRecipientAddress: z.string().trim().or(z.literal("")),
    agentPreferredLanguage: z.string().trim().or(z.literal("")),
    amountMinor: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .nonnegative({ message: t("invalidFieldError") }),
    feeAmountMinor: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .nonnegative({ message: t("invalidFieldError") }),
    agentCommissionAmountMinor: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .nonnegative({ message: t("invalidFieldError") })
      .or(z.literal(0)),
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/, { message: t("invalidFieldError") }),
    completedAt: isoDateTime(),
  });

export type SimulateTransactionCompletedFormValues = z.infer<
  ReturnType<typeof createSimulateTransactionCompletedSchema>
>;

/** Simulate transaction en échec — motif optionnel. */
export const createSimulateTransactionFailedSchema = () =>
  z.object({
    eventId: uuid(),
    transactionReference: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    partyId: uuid(),
    recipientAddress: recipientAddress(),
    preferredLanguage: preferredLanguage(),
    failureReason: z.string().trim().or(z.literal("")),
    occurredAt: isoDateTime(),
  });

export type SimulateTransactionFailedFormValues = z.infer<
  ReturnType<typeof createSimulateTransactionFailedSchema>
>;
